import React from 'react';
import { MdClose } from 'react-icons/md';

const ResultSettings = ({ onClose, settings }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-dark-bg rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-gray-200">Result Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <MdClose size={24} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Session & Term Info */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Session & {settings?.term_terminology || 'Term'} Info</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <div className="mb-2">
                  <span className="font-medium text-slate-900 dark:text-gray-200">Current Session:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-300">{settings?.current_session}</span>
                </div>
                <div className="mb-2">
                  <span className="font-medium text-slate-900 dark:text-gray-200">Current {settings?.term_terminology || 'Term'}:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-300">{settings?.current_term}</span>
                </div>
                <div>
                  <span className="font-medium text-slate-900 dark:text-gray-200">Available Sessions:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-300">{settings?.sessions?.join(', ')}</span>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">{settings?.term_terminology || 'Term'} Dates</h4>
                <ul className="text-sm text-gray-600 dark:text-gray-300">
                  {settings?.terms?.map((term, idx) => (
                    <li key={term.term} className="mb-1">
                      {settings?.term_terminology || 'Term'} {term.term}: {term.start_date ? new Date(term.start_date).toLocaleDateString() : ''} - {term.end_date ? new Date(term.end_date).toLocaleDateString() : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Assessment Section */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Assessment Structure</h3>
            
            {/* Legacy CA Structure (if exists) */}
            {settings?.assessment?.ca && !settings?.assessment?.assessment_system && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Continuous Assessment (CA)</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Total Max Score: {settings?.assessment?.ca?.max_score}</p>
                  <div className="mt-2">
                    {settings?.assessment?.ca?.cas?.map((ca, index) => (
                      <div key={ca._id} className="text-sm text-gray-600 dark:text-gray-300">
                        {ca.title ? `${ca.title}` : `CA ${index + 1}`}: {ca.max_score} marks
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                  <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Examination</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Max Score: {settings?.assessment?.exam?.max_score}</p>
                </div>
              </div>
            )}

            {/* New Assessment System Structure */}
            {settings?.assessment?.assessment_system && settings.assessment.assessment_system.length > 0 && (
              <div className="space-y-4">
                {settings.assessment.assessment_system.map((system, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center mb-4">
                      <h4 className="font-medium text-slate-900 dark:text-gray-200 capitalize mr-2">
                        {system.level_of_education} Assessment System
                      </h4>
                      {system.is_default && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 rounded-full text-xs">
                          Default
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* CA Configuration */}
                      <div>
                        <h5 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Continuous Assessment</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          Total CA Weight: <span className="font-semibold">{system.ca?.max_score}%</span>
                        </p>
                        <div className="space-y-1">
                          {system.ca?.cas?.map((ca, caIdx) => (
                            <div key={caIdx} className="text-sm text-gray-600 dark:text-gray-300">
                              <span className="font-medium">{ca.title}:</span> {ca.max_score} marks
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Exam Configuration */}
                      <div>
                        <h5 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Examination</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          Exam Weight: <span className="font-semibold">{system.exam?.max_score}%</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grading System */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Grading System(s)</h3>
            <div className="space-y-4">
              {/* New Grading System Structure */}
              {settings?.grading?.grading_system?.length > 0 ? (
                settings.grading.grading_system.map((system, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center mb-4">
                      <span className="font-medium text-slate-900 dark:text-gray-200 capitalize mr-2">
                        {system.level_of_education} Grading System
                      </span>
                      {system.is_default && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100 rounded-full text-xs">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                      {system.grades.map((grade, gidx) => (
                        <div key={gidx} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-center border border-gray-200 dark:border-gray-600">
                          <div className="text-lg font-bold text-slate-900 dark:text-gray-200">{grade.grade}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            {grade.min_score} - {grade.max_score}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : settings?.grading?.grades?.length > 0 ? (
                /* Legacy Grading Structure */
                <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center mb-4">
                    <span className="font-medium text-slate-900 dark:text-gray-200 mr-2">Legacy Grading System</span>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100 rounded-full text-xs">
                      Legacy
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {settings.grading.grades.map((grade, gidx) => (
                      <div key={gidx} className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-center border border-gray-200 dark:border-gray-600">
                        <div className="text-lg font-bold text-slate-900 dark:text-gray-200">{grade.grade}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                          {grade.min_score} - {grade.max_score}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-gray-600 dark:text-gray-300">No grading system configured.</div>
              )}
            </div>
          </div>

          {/* Remarks and Skills */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Remarks & Skills</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Available Ratings</h4>
                <div className="flex flex-wrap gap-2">
                  {settings?.remark?.ratings?.map((rating, index) => (
                    <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full text-sm">
                      {rating}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Skills Assessed</h4>
                <div className="flex flex-wrap gap-2">
                  {settings?.remark?.skills?.map((skill, index) => (
                    <span key={index} className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <h4 className="font-medium mb-2 text-slate-900 dark:text-gray-200">Remarks</h4>
              <div className="flex flex-wrap gap-2">
                {settings?.remark?.remarks?.map((remark, idx) => (
                  <span key={idx} className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100 rounded-full text-sm">
                    {remark}
                  </span>
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <div className="flex items-center">
                <span className="font-medium text-slate-900 dark:text-gray-200 mr-2">Overall Comment:</span>
                <span className={`px-2 py-1 rounded-full text-sm ${settings?.remark?.overall_comment ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.remark?.overall_comment ? 'Enabled' : 'Disabled'}</span>
              </div>
              <div className="flex items-center">
                <span className="font-medium text-slate-900 dark:text-gray-200 mr-2">Teacher Remark:</span>
                <span className={`px-2 py-1 rounded-full text-sm ${settings?.remark?.teacher_remark ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.remark?.teacher_remark ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </div>

          {/* Result Configuration */}
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl">
            <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-gray-200">Result Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-900 dark:text-gray-200">Admin Approval Required</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.admin_approval ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.result?.admin_approval ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-900 dark:text-gray-200">Teacher Preview Enabled</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.teacher_preview ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.result?.teacher_preview ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-900 dark:text-gray-200">Result Released</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.result?.is_result_released ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.result?.is_result_released ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-900 dark:text-gray-200">Cumulative Result</span>
                  <span className={`px-2 py-1 rounded-full text-sm ${settings?.cumulative_result ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{settings?.cumulative_result ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultSettings;